import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Baby, Home, Briefcase, RefreshCcw, Sparkles, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EVENTS = [
  {
    key: "svatba",
    icon: Heart,
    label: "Svatba / sňatek",
    tip: "Společné jmění mění, na co se pojištění vztahuje. Vyplatí se zkontrolovat pojistné částky domácnosti a zvážit ochranu příjmu obou partnerů.",
  },
  {
    key: "dite",
    icon: Baby,
    label: "Narození dítěte",
    tip: "S dítětem roste zodpovědnost — nejdůležitější bývá ochrana příjmu rodičů a kontrola, že pojistné částky odpovídají nové životní situaci.",
  },
  {
    key: "bydleni",
    icon: Home,
    label: "Koupě či stavba bydlení",
    tip: "K hypotéce patří pojištění nemovitosti i schopnosti splácet. Obojí se vyplatí nastavit společně s úvěrem, ne dodatečně.",
  },
  {
    key: "osvc",
    icon: Briefcase,
    label: "Začátek podnikání (OSVČ)",
    tip: "OSVČ nemá od státu nemocenskou jako zaměstnanec. Výpadek příjmu řeší právě dobře nastavené pojištění — čím dřív, tím líp.",
  },
  {
    key: "prace",
    icon: RefreshCcw,
    label: "Změna zaměstnání či příjmu",
    tip: "Změna příjmu je správný moment projít si, jestli smlouvy odpovídají novému rozpočtu — ať neplatíte zbytečně moc, ani málo.",
  },
  {
    key: "jine",
    icon: Sparkles,
    label: "Jiná životní změna",
    tip: "Každá větší životní změna je dobrý důvod smlouvy zkontrolovat. Ozvu se vám a společně probereme, co vaše situace znamená.",
  },
];

const inputCls = (err) =>
  `bg-transparent border-b ${err ? "border-red-700" : "border-[#E5E5E5]"} focus:border-[#B69A58] outline-none py-3 px-0 w-full text-base transition-colors duration-300 rounded-none placeholder-[#A3A3A3]`;

const labelCls = "block text-xs uppercase tracking-widest text-[#737373] font-medium mb-1";

const LifeEventForm = ({ event }) => {
  const openedAt = useRef(Date.now());
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | demo
  const [serverError, setServerError] = useState("");

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const next = {};
    if (form.name.trim().length < 2) next.name = "Napište prosím své jméno.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Zadejte prosím platnou e-mailovou adresu.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setStatus("sending");
    try {
      const res = await fetch(`${API}/life-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event: event.key,
          name: form.name,
          email: form.email,
          phone: form.phone,
          note: form.note,
          website: form.website,
          elapsed_ms: Date.now() - openedAt.current,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.status === "ok" || data.status === "demo")) {
        setStatus("sent");
      } else {
        setStatus("idle");
        setServerError(typeof data.detail === "string" ? data.detail : "Odeslání se nepodařilo. Zkuste to prosím znovu.");
      }
    } catch {
      setStatus("idle");
      setServerError("Odeslání se nepodařilo. Zkontrolujte prosím připojení a zkuste to znovu.");
    }
  };

  if (status === "sent") {
    return (
      <div role="status" data-testid="life-event-success" className="mt-6 border border-[#F0F0F0] p-5">
        <div className="flex items-start gap-4">
          <Check className="h-5 w-5 text-[#B69A58] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
          <p className="text-sm text-[#252525] leading-relaxed">
            Děkuji! Změnu <strong>{event.label}</strong> mám zaznamenanou a ozvu se vám.
          </p>
        </div>
        <p className="text-xs text-[#737373] leading-relaxed mt-3 border-l-2 border-[#B69A58] pl-3">
          <strong className="text-[#252525]">Co je dobré vědět hned teď:</strong> {event.tip}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="life-event-form" className="mt-6 space-y-6">
      <div>
        <label htmlFor="le-name" className={labelCls}>Jméno *</label>
        <input
          id="le-name"
          data-testid="life-event-name-input"
          type="text"
          autoComplete="name"
          value={form.name}
          onChange={set("name")}
          className={inputCls(errors.name)}
          aria-invalid={!!errors.name}
        />
        {errors.name && <p role="alert" data-testid="life-event-name-error" className="text-sm text-red-700 mt-2">{errors.name}</p>}
      </div>
      <div>
        <label htmlFor="le-email" className={labelCls}>E-mail *</label>
        <input
          id="le-email"
          data-testid="life-event-email-input"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={set("email")}
          className={inputCls(errors.email)}
          aria-invalid={!!errors.email}
        />
        {errors.email && <p role="alert" data-testid="life-event-email-error" className="text-sm text-red-700 mt-2">{errors.email}</p>}
      </div>
      <div>
        <label htmlFor="le-phone" className={labelCls}>
          Telefon <span className="normal-case font-normal">(nepovinné)</span>
        </label>
        <input
          id="le-phone"
          data-testid="life-event-phone-input"
          type="tel"
          autoComplete="tel"
          value={form.phone}
          onChange={set("phone")}
          className={inputCls(false)}
        />
      </div>
      <div>
        <label htmlFor="le-note" className={labelCls}>
          Co se mění <span className="normal-case font-normal">(nepovinné — pár slov stačí)</span>
        </label>
        <textarea
          id="le-note"
          data-testid="life-event-note-input"
          rows={3}
          value={form.note}
          onChange={set("note")}
          className={`${inputCls(false)} resize-y`}
        />
      </div>

      <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
        <label htmlFor="le-website">Webové stránky</label>
        <input
          id="le-website"
          data-testid="life-event-honeypot"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={set("website")}
        />
      </div>

      {serverError && <p role="alert" data-testid="life-event-server-error" className="text-sm text-red-700">{serverError}</p>}
      {status === "demo" && (
        <p role="status" data-testid="life-event-demo-notice" className="text-sm text-[#737373] border border-[#E5E5E5] p-4">
          Demonstrační režim: uloženo, ale e-mailové doručování zatím není nastavené.
        </p>
      )}
      <button
        type="submit"
        data-testid="life-event-submit"
        disabled={status === "sending"}
        className="w-full border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-6 py-4 text-xs uppercase tracking-widest font-medium disabled:opacity-50"
      >
        {status === "sending" ? "Odesílám…" : "Ozvěte se mi"}
      </button>
      <p className="text-xs text-[#737373] leading-relaxed">
        Odeslání je nezávazné. Údaje použiju výhradně k tomu, abych se vám mohl ozvat.{" "}
        <Link to="/ochrana-osobnich-udaju" data-testid="life-event-privacy-link" className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300">
          Informace o zpracování osobních údajů
        </Link>
      </p>
    </form>
  );
};

export const LifeEvents = () => {
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  return (
    <section id="zivotni-zmeny" data-testid="life-events-section" className="py-24 md:py-32 border-t border-[#F0F0F0] bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Hlídač změn života
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#252525] mt-6 max-w-2xl">
            Mění se vám něco důležitého?
          </h2>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-6 max-w-2xl">
            Svatba, dítě, nové bydlení nebo podnikání — každá velká změna mění i to,
            jak mají být nastavené vaše smlouvy. Vyberte, co se děje, a já se vám ozvu
            s tím, co je dobré zkontrolovat. Zabere to půl minuty.
          </p>
        </Reveal>

        <Dialog open={open} onOpenChange={setOpen}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {EVENTS.map((event, i) => {
              const Icon = event.icon;
              return (
                <Reveal key={event.key} delay={i * 0.05}>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      data-testid={`life-event-chip-${event.key}`}
                      onClick={() => setSelected(event)}
                      className="w-full h-full text-left border border-[#F0F0F0] bg-white hover:border-[#B69A58] transition-colors duration-300 p-8 flex items-center gap-5"
                    >
                      <Icon className="h-7 w-7 text-[#B69A58] shrink-0" strokeWidth={1} aria-hidden="true" />
                      <span className="font-display text-xl text-[#252525]">{event.label}</span>
                    </button>
                  </DialogTrigger>
                </Reveal>
              );
            })}
          </div>
          {selected && (
            <DialogContent className="bg-white sm:max-w-md" data-testid="life-event-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-[#252525]">{selected.label}</DialogTitle>
                <DialogDescription className="text-sm text-[#4A4A4A]">
                  Nechte mi kontakt a ozvu se vám — probereme, co vaše změna znamená pro smlouvy a finance.
                </DialogDescription>
              </DialogHeader>
              <LifeEventForm key={selected.key} event={selected} />
            </DialogContent>
          )}
        </Dialog>
      </div>
    </section>
  );
};
